import React, { useEffect, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { RecordDataIndicators } from '../../components/RecordDataIndicators';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { AppRoute, AppRoutes } from '../../routes';
import { DeviceProfileData } from '../../components/DeviceProfile/DeviceProfileData';
import { useSierraGatewayDeviceDetails } from '../../data/DataProviders/SierraGatewayDeviceDetailsProvider';
import { EditConnectedDevice } from './EditConnectedDevice';
import { SierraDeviceData } from '../../types/device';
import { isDataLoaded } from '../../types';
import { DataIndicators } from '../../components/DataIndicators';
import { GenericToolbar } from '../../components/GenericToolbar';
import { pushAppRoute } from '../../data/domain/route-manager';
import { getDeviceInfoFromMasterData } from '../../lib/device-helpers';
import { DeviceBlueprintProvider } from '../../data/DataProviders/DeviceBlueprintProvider';
import { SignalRSiteDataProvider } from '../../data/DataProviders/SignalRSiteDataProvider';
import { DeviceDetailsProvider } from '../../data/DataProviders/DeviceDetailsProvider';

export const DeviceProfile: React.FC = () => {
    const selectedOrganization = useSelectedOrganizationData();
    const currentAppRoute = useCurrentAppRoute();
    const history = useHistory();
    const { t } = useLanguageLocale();
    const sierraDeviceDetails = useSierraGatewayDeviceDetails();
    const deviceMasterData = useDeviceMaster();
    const gatewayName = isDataLoaded(sierraDeviceDetails) ? sierraDeviceDetails.value.name : '';
    const currentPage = currentAppRoute.type === 'DeviceProfile' ? 'DeviceProfile' : 'EditConnectedDevice';

    const [gatewayDeviceData, setGatewayDeviceData] = useState<SierraDeviceData | null>(null);
    const [deviceTypeList, setDeviceTypeList] = useState<any[]>([]);
    const orgName = isRecordFound(selectedOrganization) ? selectedOrganization.value.name : '';

    const updateGatewayDeviceData = (modifiedData: SierraDeviceData): void => {
        if (isDataLoaded(sierraDeviceDetails)) {
            const data = { ...sierraDeviceDetails.value };
            data.ipAddress = modifiedData.ipAddress;
            data.name = modifiedData.name;
            data.configTreeJson = modifiedData.configTreeJson;
            setGatewayDeviceData(data);
        }
    };
    useEffect(() => {
        if (isDataLoaded(sierraDeviceDetails)) {
            setGatewayDeviceData(sierraDeviceDetails.value);
        }
    }, [sierraDeviceDetails]);

    useEffect(() => {
        if (isDataLoaded(sierraDeviceDetails)) {
            deviceMasterData.getDevicesInfo(orgName, sierraDeviceDetails.value.childDeviceTypeList);
        }
    }, [gatewayDeviceData]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            isDataLoaded(sierraDeviceDetails) &&
            deviceMasterData.rowKey === sierraDeviceDetails.value.childDeviceTypeList
        ) {
            const childDeviceInfo = getDeviceInfoFromMasterData(deviceMasterData.deviceInfo.value);
            if (childDeviceInfo) {
                setDeviceTypeList(childDeviceInfo.childDeviceTypes);
            }
        }
    }, [deviceMasterData]); //eslint-disable-line react-hooks/exhaustive-deps

    const backRoute = (orgId: string, locationId: string, siteId: string, deviceId: string): AppRoute =>
        currentPage === 'DeviceProfile'
            ? { type: 'SiteProfile', orgId: orgId, locationId: locationId, siteId: siteId }
            : {
                  type: 'DeviceProfile',
                  deviceId: deviceId,
                  locationId: locationId,
                  orgId: orgId,
                  siteId: siteId,
              };

    return (
        <>
            <RecordDataIndicators
                recordData={selectedOrganization}
                errorMessage={t('DASHBOARD.ERROR_LOADING_ORGANIZATION')}
                notFoundIcon="Business"
                notFoundMessage={t('DASHBOARD.ORGANIZATION_NOT_FOUND')}
            />
            {isRecordFound(selectedOrganization) &&
                'locationId' in currentAppRoute &&
                'deviceId' in currentAppRoute &&
                'siteId' in currentAppRoute && (
                    <>
                        {(currentAppRoute.type === 'EditConnectedDevice' ||
                            currentAppRoute.type === 'DeviceProfile') && (
                            <GenericToolbar
                                showBackButton
                                backButtonOnClick={(): void => {
                                    pushAppRoute(
                                        history,
                                        backRoute(
                                            selectedOrganization.value.id,
                                            currentAppRoute.locationId,
                                            currentAppRoute.siteId,
                                            currentAppRoute.deviceId
                                        )
                                    );
                                }}
                                title={currentPage === 'DeviceProfile' ? gatewayName : 'Edit Connected Device'}
                                subtitle={currentPage === 'DeviceProfile' ? 'Connected Device Profile' : gatewayName}
                            />
                        )}
                        <DataIndicators
                            emptyIcon="Devices"
                            emptyMessage={'No Device Data'}
                            errorMessage={'No Device Data'}
                            data={sierraDeviceDetails}
                            isEmpty={false}
                        />
                        {isDataLoaded(sierraDeviceDetails) && (
                            <Switch>
                                <Route exact path={AppRoutes.DeviceProfile}>
                                    <SignalRSiteDataProvider siteId={currentAppRoute.siteId} locationId={currentAppRoute.locationId}>
                                        <DeviceDetailsProvider deviceId={currentAppRoute.deviceId} recursive={true}>
                                            <DeviceProfileData
                                                data={gatewayDeviceData}
                                                backAppRoute={backRoute(
                                                    selectedOrganization.value.id,
                                                    currentAppRoute.locationId,
                                                    currentAppRoute.siteId,
                                                    currentAppRoute.deviceId
                                                )}
                                                deviceTypeList={deviceTypeList}
                                            />
                                        </DeviceDetailsProvider>  
                                    </SignalRSiteDataProvider>  
                                </Route>
                                <Route exact path={AppRoutes.EditConnectedDevice}>
                                    <DeviceBlueprintProvider>
                                        <EditConnectedDevice
                                            sierraDeviceDetails={gatewayDeviceData}
                                            updateGatewayDeviceData={updateGatewayDeviceData}
                                            deviceTypeList={deviceTypeList}
                                        />
                                    </DeviceBlueprintProvider>
                                </Route>
                            </Switch>
                        )}
                    </>
                )}
        </>
    );
};
